import { Plus, Trash2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import type { LucideIcon } from 'lucide-react';

interface Contact {
  name?: string;
  phone?: string;
  relationship?: string;
}

interface ContactListFormProps {
  contacts: Contact[];
  onChange: (contacts: Contact[]) => void;
  icon: LucideIcon;
  title: string;
  description: string;
  addButtonLabel: string;
  emptyMessage: string;
}

export function ContactListForm({
  contacts,
  onChange,
  icon: Icon,
  title,
  description,
  addButtonLabel,
  emptyMessage,
}: ContactListFormProps) {
  const addContact = () => {
    onChange([...contacts, { name: '', phone: '', relationship: '' }]);
  };

  const updateContact = (index: number, field: string, value: string) => {
    const updated = [...contacts];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeContact = (index: number) => {
    onChange(contacts.filter((_, i) => i !== index));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Icon className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {emptyMessage}
          </p>
        ) : (
          contacts.map((contact, index) => (
            <div key={index} className="flex gap-3 items-start">
              <div className="flex-1 grid grid-cols-3 gap-3">
                <Input
                  value={contact.name || ''}
                  onChange={(e) => updateContact(index, 'name', e.target.value)}
                  placeholder="Name"
                />
                <Input
                  type="tel"
                  value={contact.phone || ''}
                  onChange={(e) => updateContact(index, 'phone', e.target.value)}
                  placeholder="Phone"
                />
                <Input
                  value={contact.relationship || ''}
                  onChange={(e) => updateContact(index, 'relationship', e.target.value)}
                  placeholder="Relationship"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeContact(index)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={addContact}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          {addButtonLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
